import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Star, Heart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { formatINR, getProductVariantPrice } from "@/lib/products";

const specLine = (product) => {
  if (product.category === "accessories") return product.description?.slice(0, 40);
  const parts = [];
  if (product.plannerType && product.plannerType !== "n/a" && product.plannerType !== "undated") {
    parts.push(product.plannerType[0].toUpperCase() + product.plannerType.slice(1));
  } else if (product.features?.includes("undated")) {
    parts.push("Undated");
  }
  const f = product.format?.filter((x) => x !== "N/A") || [];
  if (f.length) parts.push(f.join("/"));
  const c = product.cover?.filter((x) => x !== "N/A") || [];
  if (c.length) parts.push(c[0]);
  return parts.join(" · ");
};

export const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const wished = has(product.id);
  const [imgIdx, setImgIdx] = useState(0);
  const [loadGallery, setLoadGallery] = useState(false);
  const intervalRef = useRef(null);
  const isComingSoon = product.id !== "life-os-planner";
  const isOutOfStock = product.stockStatus === "out_of_stock" || product.status === "hidden" || isComingSoon;
  const displayPrice = getProductVariantPrice(product);

  const startCycle = () => {
    if (!product.images || product.images.length < 2) return;
    setLoadGallery(true);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setImgIdx((i) => (i + 1) % product.images.length);
    }, 900);
  };
  const stopCycle = () => {
    clearInterval(intervalRef.current);
    setImgIdx(0);
  };

  useEffect(() => () => clearInterval(intervalRef.current), []);

  return (
    <div
      data-testid={`product-card-${product.slug}`}
      className="group relative flex flex-col"
      onMouseEnter={startCycle}
      onMouseLeave={stopCycle}
    >
      <Link to={`/product/${product.slug}`} className="block">
        <div className="relative aspect-[4/5] bg-ivory rounded-xl overflow-hidden border border-line">
          {product.badge && (
            <span className="absolute top-3 left-3 z-10 bg-cream/95 backdrop-blur-sm text-espresso text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border border-line">
              {product.badge}
            </span>
          )}
          {isOutOfStock && (
            <span className="absolute bottom-3 left-3 z-10 bg-red-50 text-red-700 text-[10px] tracking-[0.18em] uppercase px-3 py-1 rounded-full border border-red-100">
              {isComingSoon ? "Coming soon" : "Out of stock"}
            </span>
          )}

          <button
            onClick={(e) => {
              e.preventDefault();
              toggle(product);
            }}
            data-testid={`wish-${product.slug}`}
            aria-label="Toggle wishlist"
            className={`absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-cream/95 backdrop-blur-sm border border-line flex items-center justify-center transition-colors hover:border-espresso ${
              wished ? "text-red-500" : "text-espresso"
            }`}
          >
            <Heart size={13} strokeWidth={1.5} className={wished ? "fill-current" : ""} />
          </button>

          {/* Auto-cycling image stack — fades between images */}
          {(loadGallery ? product.images : product.images?.slice(0, 1))?.map((src, i) => (
            <img
              key={src + i}
              src={src}
              alt={product.name}
              loading="lazy"
              fetchPriority={i === 0 ? "auto" : "low"}
              decoding="async"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                i === imgIdx ? "opacity-100" : "opacity-0"
              }`}
              data-testid={i === imgIdx ? `active-image-${product.slug}` : undefined}
            />
          ))}

          {/* Dot indicator on hover */}
          {product.images?.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {product.images.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${i === imgIdx ? "bg-espresso" : "bg-cream/70 border border-line"}`}
                />
              ))}
            </div>
          )}
        </div>
      </Link>

      <div className="pt-4 flex-1 flex flex-col">
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-serif text-lg text-espresso leading-tight hover:underline">
            {product.name}
          </h3>
          <p className="text-xs text-muted_brown mt-1">{specLine(product)}</p>
        </Link>

        <div className="flex items-center gap-1 mt-2 text-xs text-muted_brown">
          <Star size={11} className="fill-gold text-gold" strokeWidth={0} />
          <span>{product.id === "life-os-planner" ? "4.9 rating" : product.rating}</span>
          <span className="text-line">·</span>
          <span>{product.id === "life-os-planner" ? "167 rating" : `(${product.reviews.toLocaleString()})`}</span>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <div>
            <div className="text-espresso font-medium">{formatINR(displayPrice)}</div>
            {product.compareAtINR && (
              <div className="text-[10px] text-muted_brown line-through -mt-0.5">
                {formatINR(product.compareAtINR)}
              </div>
            )}
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              if (isOutOfStock) return;
              addItem(product);
            }}
            data-testid={`quick-add-${product.slug}`}
            disabled={isOutOfStock}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-espresso border border-line rounded-full px-3 py-2 hover:border-espresso hover:bg-ivory transition-colors disabled:opacity-50"
          >
            <Plus size={11} strokeWidth={2} /> {isComingSoon ? "Coming Soon" : isOutOfStock ? "Sold Out" : "Quick Add"}
          </button>
        </div>
      </div>
    </div>
  );
};
